import _ from 'lodash'
import { Dispatch, SetStateAction, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { FormProvider, useFormContext, useWatch } from 'react-hook-form'

import { Box, Button, Divider, Grid, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'

import JoinNetworkRequirementDialog from '@/components/ui/dialogs/JoinNetworkRequirementDialog'
import NetworkCard from '@/components/ui/cards/NetworkCard'
import { useSendNetworkJoinRequestMutation } from '@/core/services/network'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getApiErrorMessage } from '@/utils/errors'

import { useGetNetworkCards } from '../model/useGetNetworkCards'
import { SelectNetworkFormData, useSelectNetworkForm } from '../model/useSelectNetworkForm'

export default function SelectNetwork({ expertiseId, setActiveStep }: { expertiseId: string; setActiveStep: Dispatch<SetStateAction<number>> }) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { networks, networkCards, refetchNetworks } = useGetNetworkCards()
  const [requestJoinNetwork, { isLoading: isRequestingJoin }] = useSendNetworkJoinRequestMutation()
  const { rhForm, onSubmit, isSendingExpertise } = useSelectNetworkForm(expertiseId)

  const [requiredJoinNetworkId, setRequiredJoinNetworkId] = useState<string>()

  return (
    <>
      <FormProvider {...rhForm}>
        <Stack height="100%">
          <Box flex={1} p={4}>
            <NetworkCards networkCards={networkCards} networks={networks} setRequiredJoinNetworkId={setRequiredJoinNetworkId} />
          </Box>
          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between" pt={2} pb={2} pl={3} pr={3}>
            <Button onClick={() => setActiveStep((prevStep) => prevStep - 1)} variant="neutral">
              {t('common:button.back.label')}
            </Button>
            <Stack direction="row" alignItems="center" justifyContent="end" spacing={3} sx={{ flex: 1 }}>
              <LoadingButton
                color="primary"
                disabled={!rhForm.formState.isValid}
                loading={isSendingExpertise}
                onClick={() => {
                  rhForm.handleSubmit(onSubmit)()
                }}
                variant="contained"
              >
                {t('common:button.confirm.label')}
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </FormProvider>
      <JoinNetworkRequirementDialog
        isRequesting={isRequestingJoin}
        open={!!requiredJoinNetworkId}
        onClose={() => {
          setRequiredJoinNetworkId(undefined)
        }}
        onJoin={async () => {
          if (!requiredJoinNetworkId) return
          try {
            await requestJoinNetwork(requiredJoinNetworkId).unwrap()
            refetchNetworks()
            setRequiredJoinNetworkId(undefined)
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.network.requestJoin.error')), { variant: 'error' })
          }
        }}
      />
    </>
  )
}

function NetworkCards({
  networkCards,
  networks,
  setRequiredJoinNetworkId,
}: {
  networkCards: ReturnType<typeof useGetNetworkCards>['networkCards']
  networks: ReturnType<typeof useGetNetworkCards>['networks']
  setRequiredJoinNetworkId: (networkId: string) => void
}) {
  const authUser = useAppSelector(selectAuthUser)
  const rhForm = useFormContext<SelectNetworkFormData>()
  const selectedNetworkId = useWatch({ control: rhForm.control, name: 'networkId' })

  return (
    <Grid container spacing={networkCards.length > 0 ? 2 : 0}>
      {networkCards.map((networkCard) => {
        const isJoinRequestPending =
          networkCard.privacy === 'private' &&
          !!_.find(networks, ['_id', networkCard.id])?.pendingMembers.find((pendingMember) => pendingMember._id === authUser?._id)
        return (
          <Grid key={networkCard.id} item xs={12} md={6}>
            <NetworkCard
              isJoinRequestPending={isJoinRequestPending}
              network={networkCard}
              onSelect={(networkId) => {
                const network = networks?.find((network) => network._id === networkId)

                if (!network) return

                if (network.privacy === 'public' || !!_.find(network?.members, (member) => member === authUser?._id)) {
                  rhForm.setValue('networkId', networkId, { shouldValidate: true })
                } else {
                  setRequiredJoinNetworkId(network._id)
                }
              }}
              selected={networkCard.id === selectedNetworkId}
            />
          </Grid>
        )
      })}
    </Grid>
  )
}
